import { isBoolean, isFinite, isNumber, isString } from 'lodash';
import COUNTRIES from '../../constants/countries.js';
/* eslint-disable no-underscore-dangle */

/* Helpers */
function getIntOrNull(value) {
    if (isString(value)) {
        const number = parseInt(value, 10);
        return isFinite(number) ? number : null;
    }
    if (isNumber(value)) {
        return isFinite(value) ? Math.round(value) : null;
    }
    return null;
}

function getStringOrNull(value) {
    return isString(value) ? value : null;
}

/* Service */
class ConfigsService {
    constructor() {
        this.__organizationId = null;
        this.__organizationUnitId = null;
        this.__organizationUnitLocation = null;
        this.__organizationUnitName = null;
        this.__organizationUnitPhone = null;
        this.__scheduleMonday = null;
        this.__scheduleTuesday = null;
        this.__scheduleWednesday = null;
        this.__scheduleThursday = null;
        this.__scheduleFriday = null;
        this.__scheduleSaturday = null;
        this.__scheduleSunday = null;
        this.__salesDepartments = null;
        this.__websiteLogo = null;
        this.__websiteDomain = null;
        this.__websiteFacebookUrl = null;
        this.__websiteInstagramUrl = null;
        this.__websiteTwitterUrl = null;
        this.__websiteYoutubeUrl = null;
        this.__websiteId = null;
        this.__location = null;
        this.__isInMobileApp = null;
        this.__deskingToken = null;
        this.__isShowroomV25Enabled = false;
    }

    // Mutators
    setOrganizationId(organizationId) {
        this.__organizationId = getIntOrNull(organizationId);
    }

    setOrganizationUnitId(organizationUnitId) {
        this.__organizationUnitId = getIntOrNull(organizationUnitId);
    }

    setOrganizationUnitLocation(organizationUnitLocation) {
        const unitLocation = getStringOrNull(organizationUnitLocation);
        this.__organizationUnitLocation = unitLocation ? unitLocation.trim().toLowerCase() : null;
    }

    setOrganizationUnitName(organizationUnitName) {
        this.__organizationUnitName = getStringOrNull(organizationUnitName);
    }

    setOrganizationUnitPhone(organizationUnitPhone) {
        this.__organizationUnitPhone = getStringOrNull(organizationUnitPhone);
    }

    setScheduleMonday(schedule) {
        this.__scheduleMonday = getStringOrNull(schedule);
    }
    setScheduleTuesday(schedule) {
        this.__scheduleTuesday = getStringOrNull(schedule);
    }
    setScheduleWednesday(schedule) {
        this.__scheduleWednesday = getStringOrNull(schedule);
    }
    setScheduleThursday(schedule) {
        this.__scheduleThursday = getStringOrNull(schedule);
    }
    setScheduleFriday(schedule) {
        this.__scheduleFriday = getStringOrNull(schedule);
    }
    setScheduleSaturday(schedule) {
        this.__scheduleSaturday = getStringOrNull(schedule);
    }
    setScheduleSunday(schedule) {
        this.__scheduleSunday = getStringOrNull(schedule);
    }

    setSalesDepartments(salesDepartments) {
        this.__salesDepartments = getStringOrNull(salesDepartments);
    }

    setWebsiteLogo(websiteLogo) {
        this.__websiteLogo = getStringOrNull(websiteLogo);
    }

    setWebsiteDomain(websiteDomain) {
        this.__websiteDomain = getStringOrNull(websiteDomain);
    }

    setWebsiteFacebookUrl(websiteFacebookUrl) {
        this.__websiteFacebookUrl = getStringOrNull(websiteFacebookUrl);
    }

    setWebsiteInstagramUrl(websiteInstagramClientId, websiteInstagramUserId) {
        const clientId = getStringOrNull(websiteInstagramClientId);
        const userId = getStringOrNull(websiteInstagramUserId);

        if (clientId) this.__websiteInstagramUrl = clientId;
        else if (userId) this.__websiteInstagramUrl = `https://www.instagram.com/${clientId}`;
    }

    setWebsiteTwitterUrl(websiteTwitterUrl) {
        this.__websiteTwitterUrl = getStringOrNull(websiteTwitterUrl);
    }

    setWebsiteYoutubeUrl(websiteYoutubeUrl) {
        this.__websiteYoutubeUrl = getStringOrNull(websiteYoutubeUrl);
    }

    setWebsiteId(websiteId) {
        this.__websiteId = getIntOrNull(websiteId);
    }

    setLocation(location) {
        this.__location = getStringOrNull(location);
    }

    setIsInMobileApp(value) {
        this.__isInMobileApp = isBoolean(value) ? value : false;
    }

    /**
     * Set the token given by CRM to call the Showroom API
     * @param {string} deskingToken
     */
    setDeskingToken(deskingToken) {
        this.__deskingToken = isString(deskingToken) ? deskingToken : null;
    }

    setIsShowroomV25Enabled(isShowroomV25Enabled) {
        this.__isShowroomV25Enabled = isBoolean(isShowroomV25Enabled) ? isShowroomV25Enabled : false;
    }

    // Accessors
    getOrganizationId() {
        return this.__organizationId;
    }

    getOrganizationUnitId() {
        return this.__organizationUnitId;
    }

    getOrganizationUnitLocation() {
        return this.__organizationUnitLocation;
    }

    getOrganizationUnitName() {
        return this.__organizationUnitName;
    }

    getOrganizationUnitPhone() {
        return this.__organizationUnitPhone;
    }

    getScheduleMonday() {
        return this.__scheduleMonday;
    }
    getScheduleTuesday() {
        return this.__scheduleTuesday;
    }
    getScheduleWednesday() {
        return this.__scheduleWednesday;
    }
    getScheduleThursday() {
        return this.__scheduleThursday;
    }
    getScheduleFriday() {
        return this.__scheduleFriday;
    }
    getScheduleSaturday() {
        return this.__scheduleSaturday;
    }
    getScheduleSunday() {
        return this.__scheduleSunday;
    }

    getSalesDepartments() {
        return this.__salesDepartments;
    }

    getWebsiteLogo() {
        return this.__websiteLogo;
    }

    getWebsiteDomain() {
        return this.__websiteDomain;
    }

    getWebsiteFacebookUrl() {
        return this.__websiteFacebookUrl;
    }

    getWebsiteInstagramUrl() {
        return this.__websiteInstagramUrl;
    }

    getWebsiteTwitterUrl() {
        return this.__websiteTwitterUrl;
    }

    getWebsiteYoutubeUrl() {
        return this.__websiteYoutubeUrl;
    }

    getWebsiteId() {
        return this.__websiteId;
    }

    getLocation() {
        return this.__location;
    }

    getCountry() {
        const lowerCaseLocation = isString(this.__organizationUnitLocation)
            ? this.__organizationUnitLocation.toLowerCase()
            : undefined;
        const provinceObj = COUNTRIES.find((prov) => prov.code === lowerCaseLocation);
        return provinceObj ? provinceObj.country : null;
    }

    isInMobileApp() {
        return this.__isInMobileApp;
    }

    /**
     * @return {string} The token given by CRM to call the Showroom API
     */
    getDeskingToken() {
        return this.__deskingToken;
    }

    isShowroomV25Enabled() {
        return this.__isShowroomV25Enabled;
    }

    /**
     * @return {Object} An object with properties to identify the context
     */
    getLoggerContext() {
        return {
            organizationId: this.__organizationId,
            organizationUnitId: this.__organizationUnitId,
            websiteId: this.__websiteId,
            mobileApp: this.__isInMobileApp,
        };
    }
}

export default new ConfigsService();
